import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { debounceTime, Subject, takeUntil, timer } from 'rxjs';
import { WorkerAgreementDetailsDto } from '../../../models/dtos/worker-agreement-details-dto';
import { EmploymentAgreementDetailsDto } from '../../../models/dtos/employment-agreement-details-dto';
import { AuthService } from '../../../core/authentication/auth.service';
import moment from 'moment';
import { Permission } from 'src/app/common/enums';
import { Moment } from 'moment';
import { AnnexBasicDataStepModel } from 'src/app/models/dtos/annexes/annex-basic-data-step-model';
import { trigger, transition, style, animate } from '@angular/animations';

interface LoadingState {
  isLoading: boolean;
  showSuccess: boolean;
}

type BasicDataForm = FormGroup<{
  annexConclusionDate: FormControl<Date>;
  annexDateFrom: FormControl<Date | null>;
  annexDateTo: FormControl<Date | null>;
  isIndefinite: FormControl<boolean>;
}>;

type ReadOnlyDataForm = FormGroup<{
  employmentType: FormControl<string>;
  employmentDateFrom: FormControl<string>;
  employmentDateTo: FormControl<string>;
  fullName: FormControl<string>;
}>;

@Component({
  selector: 'app-basic-data-step',
  templateUrl: './basic-data-step.component.html',
  styleUrls: ['./basic-data-step.component.scss'],
  standalone: false,
  animations: [
    trigger('successAnimation', [
      transition(':enter', [style({ transform: 'scale(0)', opacity: 0 }), animate('300ms ease-out', style({ transform: 'scale(1)', opacity: 1 }))]),
      transition(':leave', [animate('200ms ease-in', style({ transform: 'scale(0)', opacity: 0 }))]),
    ]),
  ],
})
export class BasicDataStepComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private authService = inject(AuthService);

  @Input() agreement: WorkerAgreementDetailsDto | EmploymentAgreementDetailsDto;
  @Input() employmentTypeId: number;
  @Output() dataChange = new EventEmitter<AnnexBasicDataStepModel>();

  basicDataForm!: BasicDataForm;
  readOnlyDataForm!: ReadOnlyDataForm;
  loadingState: LoadingState = { isLoading: false, showSuccess: false };
  private destroy$ = new Subject<void>();

  get annexConclusionDate() {
    return this.basicDataForm.get('annexConclusionDate');
  }

  get annexDateFrom() {
    return this.basicDataForm.get('annexDateFrom');
  }

  get annexDateTo() {
    return this.basicDataForm.get('annexDateTo');
  }

  get isIndefinite() {
    return this.basicDataForm.get('isIndefinite');
  }

  ngOnInit(): void {
    this.buildFormGroup();
    this.initializeReadOnlyForm();
    this.setupFormChangeListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupFormChangeListener(): void {
    this.toggleIndefinite(this.isIndefinite.value);

    this.isIndefinite.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((isIndefinite) => {
      this.toggleIndefinite(isIndefinite);
    });

    this.basicDataForm.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(() => {
      if (this.basicDataForm.valid) {
        this.startLoading();

        this.dataChange.emit({
          AnnexStartDate: this.annexDateFrom.value,
          AnnexConclusionDate: this.annexConclusionDate.value,
          AnnexEndDate: this.isIndefinite.value ? null : this.annexDateTo.value,
        });
      }
    });

    this.annexDateFrom.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.isIndefinite.value) {
        this.annexDateTo.updateValueAndValidity();
      }
    });
  }

  private buildFormGroup(): void {
    this.basicDataForm = this.formBuilder.group({
      annexConclusionDate: this.formBuilder.nonNullable.control({ value: moment().startOf('day').toDate(), disabled: true }, { validators: [Validators.required] }),
      annexDateFrom: this.formBuilder.control<Date | null>(null, { validators: [Validators.required] }),
      annexDateTo: this.formBuilder.control<Date | null>({ value: null, disabled: true }),
      isIndefinite: this.formBuilder.control<boolean>(!this.agreement.EmploymentDateTo),
    });
  }

  private initializeReadOnlyForm(): void {
    this.readOnlyDataForm = this.formBuilder.nonNullable.group({
      employmentType: this.formBuilder.nonNullable.control({ value: this.agreement?.AgreementType ?? '', disabled: true }),
      employmentDateFrom: this.formBuilder.nonNullable.control({
        value: moment(this.agreement?.EmploymentDateFrom).format('DD.MM.YYYY'),
        disabled: true,
      }),
      employmentDateTo: this.formBuilder.control({
        value: this.agreement?.EmploymentDateTo ? moment(this.agreement?.EmploymentDateTo).format('DD.MM.YYYY') : null,
        disabled: true,
      }),
      fullName: this.formBuilder.nonNullable.control({ value: this.agreement?.FullName ?? '', disabled: true }),
    });
  }

  public annexConclusionDateFilter = (date: Moment): boolean =>
    (!this.annexDateFrom.value || date.isSameOrBefore(this.annexDateFrom.value)) && date?.isSameOrAfter(moment().startOf('day'));

  public annexDateFromFilter = (date: Moment): boolean => {
    if (!date) {
      return false;
    }

    const employmentFrom = this.agreement?.EmploymentDateFrom ? moment(this.agreement.EmploymentDateFrom) : null;
    const employmentTo = this.agreement?.EmploymentDateTo ? moment(this.agreement.EmploymentDateTo) : null;

    if (employmentFrom && date.isBefore(employmentFrom, 'day')) {
      return false;
    }

    if (employmentTo && date.isAfter(employmentTo, 'day')) {
      return false;
    }

    if (this.hasHRPermissions()) {
      return true;
    }

    return date.isSameOrAfter(this.annexConclusionDate.value, 'day');
  };
  
  public annexDateToFilter = (date: Moment | null): boolean => {
    if (!date) {
      return true;
    }

    const selectedDate = moment(date).startOf('day');
    const dateFrom = this.annexDateFrom.value;
    if (dateFrom) {
      const dateFromMoment = moment(dateFrom).startOf('day');
      if (selectedDate.isBefore(dateFromMoment)) {
        return false;
      }
    }

    if (this.agreement?.EmploymentDateTo) {
      const agreementTo = moment(this.agreement.EmploymentDateTo).startOf('day');
      if (selectedDate.isAfter(agreementTo)) {
        return false;
      }
    }

    return true;
  };

  private hasHRPermissions(): boolean {
    return this.authService.hasPermission(Permission.DisableAnnexDateValidation);
  }

  private toggleIndefinite(isIndefinite: boolean): void {
    if (isIndefinite) {
      this.annexDateTo.disable();
      this.annexDateTo.setValue(null);
      this.annexDateTo.clearValidators();
      this.annexDateTo.setErrors(null);
    } else {
      this.annexDateTo.enable();
      this.annexDateTo.setValidators([Validators.required]);
    }
    this.annexDateTo.updateValueAndValidity();
  }

  getFormData(): any {
    return this.basicDataForm.value;
  }

  startLoading(): void {
    this.loadingState = { isLoading: true, showSuccess: false };
  }

  showSuccess(): void {
    this.loadingState = { isLoading: false, showSuccess: true };

    timer(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadingState = { isLoading: false, showSuccess: false };
      });
  }

  showError(): void {
    this.loadingState = { isLoading: false, showSuccess: false };
  }
}
